import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { startAuthentication } from '@simplewebauthn/browser';
import { fetchLoginOptions, verifyLogin } from '../api/auth';
import { useToast } from '../context/ToastContext';
import { KeyRound, ShieldCheck, Fingerprint } from 'lucide-react';

export const LoginPage: React.FC = () => {
  const { refreshAuth } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);

  const handlePasskeyLogin = async () => {
    setLoading(true);
    try {
      // 1. Fetch login options
      const optionsRes = await fetchLoginOptions();
      // 2. Start WebAuthn authentication with browser
      const credential = await startAuthentication(optionsRes.publicKey);
      // 3. Verify on server
      await verifyLogin(optionsRes.challenge_id, credential);
      showToast('登录成功，欢迎回到 Lexi', 'success');
      await refreshAuth();
    } catch (err: any) {
      if (err.name === 'NotAllowedError') {
        showToast('用户取消了验证操作或请求超时', 'warning');
      } else {
        showToast(err.message || 'Passkey 验证失败，请确认设备已注册', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-slate-100/60 to-emerald-50/30 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-tr from-emerald-600 to-teal-500 text-white shadow-lg shadow-emerald-200 mb-4">
          <Fingerprint className="w-8 h-8" />
        </div>
        <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight">
          Lexi 英语学习与生词库
        </h2>
        <p className="mt-2 text-sm text-slate-600">
          基于 FSRS 间隔重复算法，实事求是的科学单词记忆平台
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-6 shadow-xl shadow-slate-200/50 sm:rounded-2xl sm:px-10 border border-slate-200/80">
          <div className="space-y-6">
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 text-center">
              <ShieldCheck className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
              <p className="text-sm font-medium text-slate-800">免密通行密钥 (Passkey)</p>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                通过指纹、面容或设备 PIN 码一键秒级认证，杜绝明文密码泄露风险。
              </p>
            </div>

            <button
              type="button"
              disabled={loading}
              onClick={handlePasskeyLogin}
              className="w-full flex items-center justify-center gap-2.5 py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50 transition-all cursor-pointer"
            >
              <KeyRound className="w-4 h-4" />
              {loading ? '等待设备生物识别验证...' : '使用 Passkey 登录'}
            </button>
          </div>

          <div className="mt-8 pt-5 border-t border-slate-100 text-center">
            <span className="text-[11px] text-slate-400">
              运行于 https://en.lipluscu.com • 现代 WebAuthn 标准
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
